import { useCallback, useEffect, useMemo, useState } from 'react';

import { ContentSourceFieldSelection, ContentSourceInfo } from '~/services/AccommodationService';

import {
  FieldState,
  buildChangedFields,
  buildFieldStates,
  hasFieldChanged,
  initFieldState,
} from './ContentSourceFieldRow';

type ContentSourcesById = Record<string, ContentSourceInfo>;

export default function useContentSourceFieldStates(
  fields: Array<ContentSourceFieldSelection> | undefined,
  availableSources: ContentSourcesById | undefined,
) {
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({});

  useEffect(() => {
    if (fields && availableSources) {
      setFieldStates(buildFieldStates(fields, availableSources));
    }
  }, [fields, availableSources]);

  const handleFieldChange = useCallback((field: string, state: FieldState) => {
    setFieldStates((prev) => ({ ...prev, [field]: state }));
  }, []);

  const hasChanges = useMemo(() => {
    if (!fields) {
      return false;
    }

    return fields.some((fieldSelection) => {
      const state = fieldStates[fieldSelection.field];
      return state && hasFieldChanged(state, fieldSelection.activeSourceId);
    });
  }, [fields, fieldStates]);

  const getFieldState = useCallback(
    (fieldSelection: ContentSourceFieldSelection) =>
      fieldStates[fieldSelection.field] ??
      initFieldState(fieldSelection.activeSourceId, availableSources, fieldSelection.valueType),
    [availableSources, fieldStates],
  );

  const getChangedFields = useCallback(() => {
    if (!fields) {
      return [];
    }

    return buildChangedFields(fields, fieldStates, {
      clearedContentSourceId: null,
    });
  }, [fields, fieldStates]);

  return {
    getChangedFields,
    getFieldState,
    handleFieldChange,
    hasChanges,
  };
}
